/**
 * 애플리케이션 진입점
 *
 * 주요 기능:
 * - React 애플리케이션 루트 렌더링
 * - TanStack Query(React Query) 클라이언트 설정 및 제공
 * - React Query Devtools 설정 (개발 환경 전용)
 * - React Strict Mode 적용 (개발 시 잠재적 문제 감지)
 *
 * React Query 설정:
 * - staleTime: 30초 (캐시 데이터 신선도 유지 시간)
 * - gcTime: 5분 (미사용 캐시 가비지 컬렉션 대기 시간)
 * - retry: 1회 (쿼리 실패 시 재시도 횟수)
 */

import React from "react";
import ReactDOM from "react-dom/client";

// 외부 라이브러리
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// 내부 모듈
import App from "./App.tsx";
import "./index.css";

// React Query 캐시 및 재시도 전략 설정
const QUERY_CONFIG = {
  STALE_TIME: 1000 * 30, // 30초 - 데이터가 신선한 상태로 유지되는 시간
  GC_TIME: 1000 * 60 * 5, // 5분 - 가비지 컬렉션 대상이 되기까지의 시간
  RETRY_COUNT: 1, // 쿼리 실패 시 재시도 횟수
} as const;

/**
 * React Query 클라이언트 인스턴스 생성
 *
 * 서버 상태 관리를 위한 중앙 집중식 클라이언트 설정
 * 캐싱, 재시도, 자동 리패치 등의 전역 옵션 적용
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_CONFIG.STALE_TIME,
      gcTime: QUERY_CONFIG.GC_TIME,
      retry: QUERY_CONFIG.RETRY_COUNT,
      refetchOnWindowFocus: true, // 윈도우 포커스 시 데이터 재요청
      refetchOnReconnect: true, // 네트워크 재연결 시 데이터 재요청
    },
    mutations: {
      retry: 0, // Mutation 실패 시 재시도하지 않음 (명시적 에러 처리 필요)
    },
  },
});

// React 애플리케이션 루트 렌더링
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>,
);
